import React, {
    createContext,
    useContext,
    useState,
    useEffect
} from "react";
import {
    getCustomerConfig,
    getReviews,
    getDashData,
    getUserInfo,
    logoutUser,
    refreshAccessToken
} from "../services/Request";
import Cookies from "js-cookie";
import { jwtDecode } from "jwt-decode";
import useIdleTimer from "../services/IdleTimer";
import dayjs from "dayjs";
import {cacheTokensWithExpiration} from "../services/Utils";

const GlobalContext = createContext();
export const useGlobalContext = () => useContext(GlobalContext);

const initialUserState = {
    username: "",
    role: "",
    actort: "",
    firstName: "",
    lastName: "",
    isViewOnlyRole: false,
};

const getStartDate = () => {
    const storedDate = sessionStorage.getItem("startDate");
    return storedDate ? dayjs(storedDate) : dayjs().subtract(7, 'day').startOf("day");
};

const getEndDate = () => {
    const storedDate = sessionStorage.getItem("endDate");
    return storedDate ? dayjs(storedDate) : dayjs().endOf("day");
};

export const GlobalProvider = (props) => {
    const [themeMode, setThemeMode] = useState(localStorage.getItem('themeMode') || "light");
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [user, setUser] = useState(initialUserState);
    const [reviews, setReviews] = useState([]);
    const [dashData, setDashData] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [checkData, setCheckData] = useState([]);
    const [isLoadingCheckData, setIsLoadingCheckData] = useState(false)
    const [apiCallSuccess, setApiCallSuccess] = useState(false);
    const [startDate, setStartDate] = useState(getStartDate());
    const [endDate, setEndDate] = useState(getEndDate());
    const [tabStatus, setTabStatus] = useState("Pending");
    const [updatedDataTime, setUpdatedDataTime] = useState(new Date(Date.now() - 1000));
    const [reviewIds] = useState([]);
    const isIdle = useIdleTimer();
    const [customerConfigs, setCustomerConfigs] = useState({});
    const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);
    const [alertDialogText, setAlertDialogText] = useState('');

    const openAlertDialog = (text) => {
        setAlertDialogText(text);
        setIsAlertDialogOpen(true);
    };

    const closeAlertDialog = () => {
        setIsAlertDialogOpen(false);
    };
    
    const clearStorageAndCookies = () => {
        clearItems(localStorage, ["refreshToken", "allowedSources", "username", "lastActivity"]);
        clearItems(sessionStorage, ["startDate", "endDate"]);
        clearItems('cookies', ["accessToken"]);
        setStartDate(dayjs().subtract(7, 'day').startOf("day"));
        setEndDate(dayjs().endOf("day"));
        setIsLoggedIn(false);
        setUser(initialUserState);
        setCheckData([]);
        setDashData([]);
    };

    const logout = () => {
        const response = logoutUser().catch(OnLogoutError);
        clearStorageAndCookies();
        return response;
    };
    
    const checkForToken = async () => {
        const refreshToken = localStorage.getItem("refreshToken");
        let accessToken = Cookies.get("accessToken");
        try {
            if (!refreshToken) {
                await logout();
                return false;
            }
            
            // Refresh token if available, otherwise logout.
            if (!accessToken && refreshToken) {
                const response = await refreshAccessToken(refreshToken);
                if (response.status === 200) {
                    accessToken = response.data.shortTermAccessToken;
                    await setTokens(response);
                }
            }

            const decoded = jwtDecode(accessToken);
            if (decoded && isLoggedIn !== true) {
                setUserDetails(decoded);
                setIsLoggedIn(true);
            }
            
            getCustomerConfigs();
        } catch (err) {
            console.error("JWT decoding failed", err);
            await logout();
            return false;
        }

        return true;
    };

    const setTokens = (authResponse) => {
        // Get token initialization and expiration time and convert to minutes for cookie expiration
        cacheTokensWithExpiration(authResponse);
        checkForToken();
    }

    function setUserDetails(decoded) {
        localStorage.setItem("username", decoded.unique_name);

        getUserInfo(decoded.unique_name).then(OnGetUserSuccess).catch(OnGetUserError);
        function OnGetUserSuccess(response) {
            setUser({
                username: decoded.unique_name,
                role: decoded.role,
                actort: decoded.actort,
                firstName: response.data.firstName,
                lastName: response.data.lastName,
                isViewOnlyRole: decoded.role.toLowerCase() === "viewer",
            });
        }
        
        function OnGetUserError(error) {
            console.error("Error getting user info", error);
            return logout();
        }
    }
    
    // @description Get customer and TrueChecks configs from the API and set the state
    async function getCustomerConfigs() {
        try {
            const response = await getCustomerConfig();
            setCustomerConfigs(response.data);
        } catch (error) {
            console.error("Error getting customer config", error);
        }
    }

    function clearItems(storageType, keys) {
        if (storageType === 'cookies') {
            keys.forEach(key => Cookies.remove(key));
        } else {
            keys.forEach(key => storageType.removeItem(key));
        }
    }
    
    function OnLogoutError(e) {
        console.error("There was a failure while submitting the logout request to the API:" + e);
    }

    useEffect(() => {
        if (isIdle && isLoggedIn) {
            openAlertDialog("You have been logged out due to inactivity.");
            logout();
        }
    }, [isIdle]);

    /**
     Fetches reviews and transactions, combines them into a single object, and sets the resulting data as check data.
     */
    const getCheckData = async () => {
        setReviews([]);
        setTransactions([]);
        reviewIds.length = 0;
        setIsLoadingCheckData(true);

        try {
            // Get all reviews
            if (isLoggedIn !== true){
                return;
            }
            const dash = await getDashData("all", startDate, endDate);
            setDashData(dash || []);
            
            const reviewTrans = await getReviews("all", startDate, endDate);
            setCheckData(reviewTrans || []);
         
            setUpdatedDataTime(new Date(Date.now() - 1000));
            setApiCallSuccess(true);
        } catch (e) {
            setCheckData([])
            setApiCallSuccess(false)
            console.error("Error getting check data: " + e)
        } finally {
            setIsLoadingCheckData(false)
        }
    };
    
    // Handle tab change to display the correct tab
    const handleTabStatus = (event, newTabStatus) => {
        setTabStatus(newTabStatus);
    };

    useEffect(() => {
        sessionStorage.setItem("startDate", startDate);
        sessionStorage.setItem("endDate", endDate);
        getCheckData();
    }, [startDate, endDate, isLoggedIn]);

    return (
        <GlobalContext.Provider
            value={{
                themeMode,
                setThemeMode,
                isLoggedIn,
                setIsLoggedIn,
                reviews,
                transactions,
                checkData,
                getCheckData,
                dashData,
                checkForToken,
                setTokens,
                user,
                logout,
                startDate,
                setStartDate,
                endDate,
                setEndDate,
                updatedDataTime,
                tabStatus,
                handleTabStatus,
                customerConfigs,
                getCustomerConfigs,
                clearStorageAndCookies,
                apiCallSuccess,
                openAlertDialog,
                closeAlertDialog,
                alertDialogText,
                setAlertDialogText,
                isAlertDialogOpen,
                isLoadingCheckData,
                setTabStatus
            }}
        >
            {props.children}
        </GlobalContext.Provider>
    );
};
